import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { DashboardFilterType } from '@app/reducers/page-filters/page-filters-dashboard.config';
import { selectAllowedDashboardFilter } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { ContractService } from '@reducers/orm/contract/contract.service';
import { PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { mgcIconFileCertificateCuteRe } from '@sb/mgc-icons/mgcIcon-file_certificate_cute_re.icon';
import { DashboardWidgetModule } from '@sb/ui/dashboard-widget/dashboard-widget.module';
import { IconRegistry } from '@sb/ui/icon';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addMonths } from 'date-fns';
import { finalize } from 'rxjs/operators';

import { getEndingContractsForAuthenticatedUser } from '../widget.helper';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ending-contracts-widget',
  templateUrl: './ending-contracts-widget.component.html',
  imports: [DashboardWidgetModule, TranslatePipe, LocaleDatePipe, RouterLink],
})
export class EndingContractsWidgetComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private contractService = inject(ContractService);
  private pageFilterService = inject(PageFiltersService);
  private iconRegistry = inject(IconRegistry);
  private translate = inject(TranslateService);

  periodOptions = [
    { label: this.translate.instant('month'), value: 1 },
    { label: this.translate.instant('2 months'), value: 2 },
    { label: this.translate.instant('3 months'), value: 3 },
    { label: this.translate.instant('4 months'), value: 4 },
  ];

  isLoading = signal(false);
  employeesWithContracts = computed(() =>
    this.store.selectSignal(
      getEndingContractsForAuthenticatedUser(
        toISODateString(new Date()),
        toISODateString(addMonths(new Date(), this.widgetPeriod())),
      ),
    )(),
  );

  private filterType = DashboardFilterType.ENDING_CONTRACTS;
  private dashboardFilter = this.store.selectSignal(selectAllowedDashboardFilter(this.filterType));
  widgetPeriod = computed(() => this.dashboardFilter()?.widgetPeriod ?? 1);

  constructor() {
    this.iconRegistry.registerIcons([mgcIconFileCertificateCuteRe]);
  }

  ngOnInit() {
    this.isLoading.set(true);
    void this.contractService
      .load({
        minDate: toISODateString(new Date()),
        // Max period
        maxDate: toISODateString(addMonths(new Date(), 4)),
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  onPeriodChange(period: number | string) {
    this.pageFilterService.setFilter('dashboard', this.filterType, {
      widgetPeriod: period,
    });
  }
}
